import { useState } from 'react'
import { Api } from '~/core/trpc'
import { AppSessionType } from '~/features/appSession/common'
import { Toolbox } from '~/plugins/toolbox/shared'
import { OnboardingStepProps } from '../types'

type Props = {
  appSession: AppSessionType
  valuesDefault?: any
  onSubmit: () => void
}

export const useLogic = ({ appSession, valuesDefault, onSubmit }: Props) => {
  const triggerEventMutation = Api.dataConnector.triggerEvent.useMutation()

  const update = Api.appSession.update.useMutation()

  const [index, setIndex] = useState(0)

  const [values, setValues] = useState<any>({
    ...valuesDefault,
    ...appSession.values.onboarding,
  })

  const triggerHttpEvent = async (eventKey: string) => {
    await triggerEventMutation.mutateAsync({
      appId: appSession.appId,
      providerKey: 'http',
      providerTriggerKey: 'NewRequest',
      appSessionId: appSession.id,
      payload: {
        eventKey,
        _version: 2,
      },
    })
  }

  const handleChange = (valuesUpdated: any) => {
    setValues(valuesUpdated)
  }

  const save = async (valuesUpdated: any, stepKey?: string) => {
    setValues(valuesUpdated)

    try {
      const onboardingUpdated = Toolbox.Utility.trimNullValues({
        ...appSession.values.onboarding,
        ...valuesUpdated,
      })

      await update.mutateAsync({
        where: {
          id: appSession.id,
        },
        data: {
          values: {
            ...appSession.values,
            onboarding: onboardingUpdated,
          },
        },
      })

      if (stepKey) {
        await triggerHttpEvent(`onboarding.${stepKey}.completed`)
      }
    } catch (error) {
      console.error('Unknown Error', error)
    }
  }

  const submit = async (valuesUpdated: any, stepKey?: string) => {
    await save(valuesUpdated, stepKey)

    const canSubmit = appSession?.statusOnboarding === 'PENDING'

    if (canSubmit) {
      await triggerHttpEvent(`onboarding.form.completed`)

      await update.mutateAsync({
        where: { id: appSession.id },
        data: {
          statusOnboarding: 'SUBMITTED',
        },
      })
    }

    onSubmit()
  }

  const next = () => {
    setIndex(index + 1)
  }

  const back = () => {
    setIndex(index - 1)
  }

  const step: OnboardingStepProps = {
    values: values,
    onChange: handleChange,
  }

  return {
    index,
    setIndex,
    next,
    back,
    values,
    submit,
    save,
    step,
  }
}
